import {
    Menu as ReachMenu,
    MenuButton as ReachMenuButton,
    MenuItem as ReachMenuItem,
    MenuList as ReachMenuList,
} from '@reach/menu-button';
import { Fragment } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import { ApprovalRequestMessage } from '../../../../types/ApprovalRequestMessage';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { IconChevronDown } from '../../../common/icons/cdl/ChevronDown';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { MetaData } from '../MetaData';

import { ApprovalRequestMenuItem } from './ApprovalRequestMenuItem';
import { ApprovalRequestStateTag } from './ApprovalRequestStateTag';

const ClickableArea = styled.div`
    display: flex;
    align-items: center;
    background-color: transparent;
    border: 1px solid ${theme.colors.border.muted};
    border-radius: 4px;
    padding: ${theme.space[8]};
    width: 350px;
    user-select: none;
    cursor: pointer;
    height: ${theme.space[32]};
    gap: ${theme.space[8]};
`;

const ApprovalRequestsMenuStyles = createGlobalStyle`
    /* Reach Menu renders the open menu in a portal outside the component subtree, so these styles must
       remain global even though it makes sense to keep them colocated with the dropdown. */
    [data-reach-menu] {
        display: block;
        position: absolute;
        margin-top: ${theme.space[8]};
    }

    [data-reach-menu-button] {
        background-color: transparent;
        border: none;
        outline: none;
    }

    [data-reach-menu-list] {
        display: block;
        white-space: nowrap;
        outline: none;
        border: 1px solid ${theme.colors.border.muted};
        border-radius: ${theme.radii[4]};
        box-shadow: ${theme.shadows.large};
        background-color: ${theme.colors.background.default};
    }

    [data-reach-menu-item] {
        cursor: pointer;
        display: block;
        background-color: ${theme.colors.background.default};
        color: ${theme.colors.foreground.default};
        font-size: ${theme.fontSizes[14]};
        border-bottom: 1px solid ${theme.colors.border.muted};
    }

    [data-reach-menu-item]:hover,
    [data-reach-menu-item][data-selected] {
        background-color: ${theme.colors.background.subtle};
        color: ${theme.colors.foreground.default};
        outline: none;
    }

    [data-reach-menu-item]:first-child {
        border-top-left-radius: ${theme.radii[4]};
        border-top-right-radius: ${theme.radii[4]};
    }

    [data-reach-menu-item]:last-child {
        border-bottom: 0;
        border-bottom-left-radius: ${theme.radii[4]};
        border-bottom-right-radius: ${theme.radii[4]};
    }
`;

interface MenuButtonProps {
    approvalRequest: ApprovalRequestMessage;
}

const MenuButton = ({ approvalRequest }: MenuButtonProps) => {
    return (
        <ClickableArea>
            <ApprovalRequestStateTag approvalRequest={approvalRequest} showTooltip={false} />
            <MetaData>
                {translate('approvalRequest.dropdown.latestActivity', {
                    date: formatDate({
                        date: approvalRequest.dateUpdated,
                    }),
                })}
            </MetaData>
            <Box marginLeft="auto" color="foreground.subtle">
                <IconChevronDown />
            </Box>
        </ClickableArea>
    );
};

interface ApprovalRequestsDropdownProps {
    approvalRequests: ApprovalRequestMessage[];
}

export const ApprovalRequestsDropdown = ({ approvalRequests }: ApprovalRequestsDropdownProps) => {
    const latestApprovalRequest = approvalRequests[0];

    return (
        <>
            <ApprovalRequestsMenuStyles />
            <ReachMenu>
                {() => (
                    <Fragment>
                        <ReachMenuButton>
                            <MenuButton approvalRequest={latestApprovalRequest} />
                        </ReachMenuButton>
                        <ReachMenuList style={{ position: 'relative', zIndex: theme.zIndex.interactive }}>
                            {approvalRequests.map((approvalRequest) => (
                                <ReachMenuItem
                                    style={{ minWidth: '500px', maxWidth: '600px' }}
                                    key={approvalRequest.id}
                                    onSelect={() => {}}
                                >
                                    <ApprovalRequestMenuItem approvalRequest={approvalRequest} />
                                </ReachMenuItem>
                            ))}
                        </ReachMenuList>
                    </Fragment>
                )}
            </ReachMenu>
        </>
    );
};
